import React, { useEffect, useState } from 'react';
import BtnReturnBack from '/react4xp/parts/express-help/BtnReturnBack';
import {
    Checkbox,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Row,
    Space,
    Table,
} from 'antd';
import Button from '/react4xp/mixins/ui/button/button';
import { IQuestionData, IShownComponents, TServiceUrl } from '../express-help.types';
import Link from 'antd/es/typography/Link';
import EditQuestionnaireModal from '../modals/modal-edit-poll';
import { getRandomSecure } from 'mixins/helpers';
import {
    createQuestionnaire,
    deleteQuestionById,
    getQuestionsByQuestionnaireId,
} from '/react4xp/parts/express-help/actions';

const CreatePoll = (props: any) => {
    const [createdQuestionnaireId, setCreatedQuestionnaireId] = useState<number>();
    const [isBeingDeleted, setIsBeingDeleted] = useState(false);
    const [selectedObject, setSelectedObject] = useState<IQuestionData>();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [questionnaireIsPublished, setQuestionnaireIsPublished] = useState<boolean>(false);
    const [data, setData] = useState<IQuestionData[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const serviceType: TServiceUrl = { service: 'Question' };

    const { confirm } = Modal;
    const [form] = Form.useForm();

    const deleteOne = (id: number) => {
        confirm({
            title: 'Подтвердите действия удаления данных',
            onOk() {
                setIsBeingDeleted(true);
                deleteQuestionById(id)
                    .then(() => {
                        notification['success']({
                            message: 'Данные успешно удалены!',
                        });
                        load();
                    })
                    .catch(() => {
                        notification['error']({
                            message: 'Ошибка',
                            description:
                                'Невозможно удалить запись. Требуется сначало удалить вопросы и ответы опроса',
                        });
                    })
                    .finally(() => setIsBeingDeleted(null));
            },
        });
    };

    const edit = (doc: any) => {
        setIsModalOpen(true);
        setSelectedObject(doc);
    };

    const add = (docId: number) => {
        moveToScreen({
            mainPageShown: false,
            pollPageShown: false,
            questionPageShown: false,
            answerPageShown: true,
            idOfQuestionnaire: docId,
            editMode: false,
        });
    };

    const moveToScreen = (nextScreenParameter: IShownComponents) => {
        props.showNext(nextScreenParameter);
    };

    const handleOk = () => {
        setSelectedObject(null);
        setIsModalOpen(false);
        load();
    };

    const handleCancel = () => {
        setSelectedObject(null);
        setIsModalOpen(false);
    };

    const onActionClick = (key: string, doc: any) => {
        switch (key) {
            case 'EDIT':
                edit(doc);
                break;
            case 'DELETE':
                deleteOne(doc.id);
                break;
            case 'ADD':
                add(doc.id);
                break;
        }
    };

    const columns = [
        {
            title: 'ИД',
            dataIndex: 'id',
        },
        {
            title: 'Наименование на каз. языке',
            dataIndex: 'questionKk',
        },
        {
            title: 'Наименование на рус. языке',
            dataIndex: 'questionRu',
        },
        {
            title: 'Наименование на анг. языке',
            dataIndex: 'questionEn',
        },
        {
            title: 'Изменил',
            dataIndex: 'username',
        },
        {
            title: 'Действия',
            width: 180,
            render: (text, record) => (
                <span>
                    <Link
                        className="column-button"
                        style={{ marginRight: 3 }}
                        onClick={() => {
                            onActionClick('DELETE', record);
                        }}
                    >
                        Удалить
                    </Link>

                    <Link
                        className="column-button"
                        style={{ marginRight: 3 }}
                        onClick={() => {
                            onActionClick('EDIT', record);
                        }}
                    >
                        Редактировать
                    </Link>

                    <Link
                        className="column-button"
                        style={{ marginRight: 3 }}
                        onClick={() => {
                            onActionClick('ADD', record);
                        }}
                    >
                        Добавить
                    </Link>
                </span>
            ),
        },
    ];

    const onFinish = async (values: any) => {
        setIsSaving(true);

        values.username = props.user.login;
        values.externalQuestionnaireId = 'P_' + Number.parseInt('' + getRandomSecure() * 100000000);

        createQuestionnaire(values)
            .then((response) => {
                notification['info']({
                    message: 'Опрос создан. Теперь можете добавлять вопросы!',
                });
                setCreatedQuestionnaireId(response.data.id);
            })
            .finally(() => setIsSaving(false));
    };

    const load = () => {
        if (createdQuestionnaireId) {
            getQuestionsByQuestionnaireId(createdQuestionnaireId).then((response) => {
                setData(response.data.content);
            });
        }
    };

    useEffect(() => {
        load();
    }, [createdQuestionnaireId]);

    const onReturnBackHandler = () => {
        const nextPageParameter: IShownComponents = {
            mainPageShown: true,
            pollPageShown: false,
            questionPageShown: false,
            answerPageShown: false,
            editMode: false,
        };
        props.showNext(nextPageParameter);
    };

    const onPublishedHandler = () => {
        setQuestionnaireIsPublished(!questionnaireIsPublished);
    };

    return (
        <div className="poll-screen">
            <BtnReturnBack handler={onReturnBackHandler} />

            <h4 className="my-3">Создать опрос</h4>

            <Form
                className="my-3"
                form={form}
                layout="vertical"
                onFinish={onFinish}
            >
                <Row gutter={{ xs: 8, md: 24 }}>
                    <Col
                        xs={24}
                        md={4}
                    >
                        <Form.Item
                            name="questionnaireWeight"
                            label="Вес опроса"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder="Введите"
                                max={999}
                                min={0}
                                defaultValue={0}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="questionnaireKk"
                            label="Наименование на каз. языке"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input placeholder="Введите" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="questionnaireRu"
                            label="Наименование на рус. языке"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input placeholder="Введите" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="questionnaireEn"
                            label="Наименование на анг. языке"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input placeholder="Введите" />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="published"
                            label=""
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Checkbox
                                onChange={onPublishedHandler}
                                value={questionnaireIsPublished}
                            >
                                Опубликовано
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <Space wrap>
                    <Button
                        onClick={() => {
                            const nextPageParameter: IShownComponents = {
                                mainPageShown: true,
                                pollPageShown: false,
                                questionPageShown: false,
                                answerPageShown: false,
                                editMode: false,
                            };

                            props.showNext(nextPageParameter);
                        }}
                    >
                        Отменить
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        color="gold"
                        loading={isSaving}
                    >
                        Сохранить
                    </Button>
                </Space>
            </Form>

            {createdQuestionnaireId && (
                <Row
                    gutter={{ xs: 24, md: 24 }}
                    justify="space-between"
                    className="my-3"
                >
                    <Col>
                        <h4>Список вопросов</h4>
                    </Col>
                    <Col>
                        <Button
                            disabled={!createdQuestionnaireId}
                            color="gold"
                            onClick={() => {
                                const nextPageParameter: IShownComponents = {
                                    mainPageShown: false,
                                    pollPageShown: false,
                                    questionPageShown: true,
                                    answerPageShown: false,
                                    idOfQuestionnaire: createdQuestionnaireId,
                                    editMode: false,
                                };

                                props.showNext(nextPageParameter);
                            }}
                        >
                            Добавить вопрос
                        </Button>
                    </Col>
                </Row>
            )}

            {createdQuestionnaireId && (
                <Table
                    className="my-3"
                    dataSource={data}
                    columns={columns}
                    scroll={{
                        x: true,
                    }}
                />
            )}

            <EditQuestionnaireModal
                isModalOpen={isModalOpen}
                handleOk={handleOk}
                handleCancel={handleCancel}
                selectedObject={selectedObject}
                selectedType={serviceType}
            />
        </div>
    );
};
export default CreatePoll;
